/**
 * Type Registry — metadata for port data types.
 *
 * Defines how types are edited and displayed in the visual editor.
 * Types can declare their default values, editors, preview formatters, etc.
 */

import type { PortDataType } from '../types';
import { defaultValueFor } from './PortTypes';

// ============================================================================
// Type Metadata
// ============================================================================

/**
 * Editor kind for inline editing in node widgets.
 *
 * - 'none': no inline editor (e.g., entity handles, opaque types)
 * - 'number': single numeric input
 * - 'string': text input
 * - 'bool': checkbox
 * - 'vec2': x, y inputs
 * - 'vec3': x, y, z inputs
 * - 'vec4': x, y, z, w inputs
 * - 'color': color picker (RGBA)
 */
export type EditorKind =
  | 'none'
  | 'number'
  | 'string'
  | 'bool'
  | 'vec2'
  | 'vec3'
  | 'vec4'
  | 'color';

/**
 * Component definition for multi-field types (vec2, vec3, etc.)
 */
export interface TypeComponent {
  /** Component ID (e.g., 'x', 'y', 'z', 'w', 'r', 'g', 'b', 'a') */
  readonly id: string;
  /** Display label */
  readonly label: string;
  /** Min value (optional) */
  readonly min?: number;
  /** Max value (optional) */
  readonly max?: number;
  /** Step for numeric input */
  readonly step?: number;
}

/**
 * Metadata describing how a type behaves in the editor.
 */
export interface TypeMetadata {
  /** Type identifier (matches PortDataType) */
  readonly type: PortDataType;

  /** Human-readable name */
  readonly name: string;

  /** Can this type show preview values on output ports? */
  readonly canPreview: boolean;

  /** Can this type be edited inline on input ports? */
  readonly canEdit: boolean;

  /** Editor kind to use for inline editing */
  readonly editor: EditorKind;

  /** For multi-component types, the component definitions */
  readonly components?: readonly TypeComponent[];

  /** Default value for this type */
  readonly defaultValue: unknown;

  /**
   * Format a value for preview display.
   * Returns a short string suitable for showing in the node widget.
   */
  formatPreview(value: unknown): string;

  /**
   * Parse a string value into the proper type.
   * Used when editing numeric components.
   */
  parseValue?(raw: string, componentId?: string): unknown;

  /** Color used in the editor (port dots, wires) */
  readonly color: string;
}

// ============================================================================
// Built-in Type Definitions
// ============================================================================

const BUILTIN_TYPES: TypeMetadata[] = [
  // ── Flow (no data) ──
  {
    type: 'flow',
    name: 'Flow',
    canPreview: false,
    canEdit: false,
    editor: 'none',
    defaultValue: undefined,
    formatPreview: () => '',
    color: '#ffffff',
  },

  // ── Boolean ──
  {
    type: 'bool',
    name: 'Boolean',
    canPreview: true,
    canEdit: true,
    editor: 'bool',
    defaultValue: false,
    formatPreview: (v) => (v ? 'true' : 'false'),
    color: '#e74c3c',
  },

  // ── Integers ──
  {
    type: 'i32',
    name: 'Integer',
    canPreview: true,
    canEdit: true,
    editor: 'number',
    defaultValue: 0,
    formatPreview: (v) => String(Math.trunc(v as number)),
    parseValue: (raw) => parseInt(raw, 10) || 0,
    color: '#3498db',
  },

  // ── Floats ──
  {
    type: 'f32',
    name: 'Float',
    canPreview: true,
    canEdit: true,
    editor: 'number',
    defaultValue: 0,
    formatPreview: (v) => (v as number).toFixed(2),
    parseValue: (raw) => parseFloat(raw) || 0,
    color: '#2ecc71',
  },
  {
    type: 'f64',
    name: 'Double',
    canPreview: true,
    canEdit: true,
    editor: 'number',
    defaultValue: 0,
    formatPreview: (v) => (v as number).toFixed(2),
    parseValue: (raw) => parseFloat(raw) || 0,
    color: '#27ae60',
  },
  {
    type: 'numeric',
    name: 'Numeric',
    canPreview: true,
    canEdit: true,
    editor: 'number',
    defaultValue: 0,
    formatPreview: (v) => (v as number).toFixed(2),
    parseValue: (raw) => parseFloat(raw) || 0,
    color: '#1abc9c',
  },

  // ── String ──
  {
    type: 'string',
    name: 'String',
    canPreview: true,
    canEdit: true,
    editor: 'string',
    defaultValue: '',
    formatPreview: (v) => {
      const s = String(v ?? '');
      return s.length > 12 ? s.slice(0, 12) + '…' : s;
    },
    color: '#f1c40f',
  },

  // ── Vec2 ──
  {
    type: 'vec2',
    name: 'Vector 2',
    canPreview: true,
    canEdit: true,
    editor: 'vec2',
    components: [
      { id: 'x', label: 'X', step: 0.1 },
      { id: 'y', label: 'Y', step: 0.1 },
    ],
    defaultValue: { x: 0, y: 0 },
    formatPreview: (v) => {
      const vec = v as { x: number; y: number };
      return `${vec.x.toFixed(1)}, ${vec.y.toFixed(1)}`;
    },
    parseValue: (raw, cid) => {
      const n = parseFloat(raw) || 0;
      // Returns partial update — caller merges with existing value
      return { [cid!]: n };
    },
    color: '#9b59b6',
  },

  // ── Vec3 ──
  {
    type: 'vec3',
    name: 'Vector 3',
    canPreview: true,
    canEdit: true,
    editor: 'vec3',
    components: [
      { id: 'x', label: 'X', step: 0.1 },
      { id: 'y', label: 'Y', step: 0.1 },
      { id: 'z', label: 'Z', step: 0.1 },
    ],
    defaultValue: { x: 0, y: 0, z: 0 },
    formatPreview: (v) => {
      const vec = v as { x: number; y: number; z: number };
      return `${vec.x.toFixed(1)}, ${vec.y.toFixed(1)}, ${vec.z.toFixed(1)}`;
    },
    parseValue: (raw, cid) => ({ [cid!]: parseFloat(raw) || 0 }),
    color: '#8e44ad',
  },

  // ── Vec4 ──
  {
    type: 'vec4',
    name: 'Vector 4',
    canPreview: true,
    canEdit: true,
    editor: 'vec4',
    components: [
      { id: 'x', label: 'X', step: 0.1 },
      { id: 'y', label: 'Y', step: 0.1 },
      { id: 'z', label: 'Z', step: 0.1 },
      { id: 'w', label: 'W', step: 0.1 },
    ],
    defaultValue: { x: 0, y: 0, z: 0, w: 0 },
    formatPreview: (v) => {
      const vec = v as { x: number; y: number; z: number; w: number };
      return `${vec.x.toFixed(0)}, ${vec.y.toFixed(0)}, …`;
    },
    parseValue: (raw, cid) => ({ [cid!]: parseFloat(raw) || 0 }),
    color: '#6c3483',
  },

  // ── Quaternion ──
  {
    type: 'quat',
    name: 'Quaternion',
    canPreview: true,
    canEdit: true,
    editor: 'vec4',
    components: [
      { id: 'x', label: 'X', step: 0.01 },
      { id: 'y', label: 'Y', step: 0.01 },
      { id: 'z', label: 'Z', step: 0.01 },
      { id: 'w', label: 'W', step: 0.01 },
    ],
    defaultValue: { x: 0, y: 0, z: 0, w: 1 },
    formatPreview: (v) => {
      const q = v as { x: number; y: number; z: number; w: number };
      return `${q.x.toFixed(2)}, ${q.y.toFixed(2)}, …`;
    },
    parseValue: (raw, cid) => ({ [cid!]: parseFloat(raw) || 0 }),
    color: '#e67e22',
  },

  // ── Color ──
  {
    type: 'color',
    name: 'Color',
    canPreview: true,
    canEdit: true,
    editor: 'color',
    components: [
      { id: 'r', label: 'R', min: 0, max: 255, step: 1 },
      { id: 'g', label: 'G', min: 0, max: 255, step: 1 },
      { id: 'b', label: 'B', min: 0, max: 255, step: 1 },
      { id: 'a', label: 'A', min: 0, max: 255, step: 1 },
    ],
    defaultValue: { r: 255, g: 255, b: 255, a: 255 },
    formatPreview: (v) => {
      if (typeof v === 'number') {
        // Packed ABGR
        const r = (v >>> 0) & 0xff;
        const g = (v >>> 8) & 0xff;
        const b = (v >>> 16) & 0xff;
        return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
      }
      const c = v as { r: number; g: number; b: number; a: number };
      return `#${c.r.toString(16).padStart(2, '0')}${c.g.toString(16).padStart(2, '0')}${c.b.toString(16).padStart(2, '0')}`;
    },
    parseValue: (raw, cid) => ({ [cid!]: Math.max(0, Math.min(255, parseInt(raw, 10) || 0)) }),
    color: '#e91e63',
  },

  // ── Entity (no inline edit) ──
  {
    type: 'entity',
    name: 'Entity',
    canPreview: false,
    canEdit: false,
    editor: 'none',
    defaultValue: -1,
    formatPreview: () => '',
    color: '#00bcd4',
  },

  // ── GameObject reference ──
  {
    type: 'gameobject',
    name: 'GameObject',
    canPreview: true,
    canEdit: false,
    editor: 'none',
    defaultValue: -1,
    formatPreview: (v) => (v as number) === -1 ? 'None' : `#${v}`,
    color: '#2ecc71',
  },

  // ── Any (wildcard) ──
  {
    type: 'any',
    name: 'Any',
    canPreview: false,
    canEdit: false,
    editor: 'none',
    defaultValue: null,
    formatPreview: (v) => (v == null ? '∅' : String(v)),
    color: '#78909c',
  },
];

// ============================================================================
// Registry
// ============================================================================

const registry = new Map<PortDataType, TypeMetadata>();

// Initialize with built-in types
for (const meta of BUILTIN_TYPES) {
  registry.set(meta.type, meta);
}

/**
 * Fallback metadata for unknown types.
 */
const UNKNOWN_TYPE: TypeMetadata = {
  type: 'any',
  name: 'Unknown',
  canPreview: false,
  canEdit: false,
  editor: 'none',
  defaultValue: null,
  formatPreview: () => '?',
  color: '#607d8b',
};

/**
 * Register a custom type with its metadata.
 *
 * @example
 * registerTypeMeta({
 *   type: 'customVec',
 *   name: 'Custom Vector',
 *   canPreview: true,
 *   canEdit: true,
 *   editor: 'vec2',
 *   components: [
 *     { id: 'x', label: 'X', step: 1 },
 *     { id: 'y', label: 'Y', step: 1 },
 *   ],
 *   defaultValue: { x: 0, y: 0 },
 *   formatPreview: (v) => `${v.x}, ${v.y}`,
 *   color: '#ff5722',
 * });
 */
export function registerTypeMeta(meta: TypeMetadata): void {
  registry.set(meta.type, meta);
}

/**
 * Get metadata for a type.
 * Returns fallback metadata if the type is not registered.
 */
export function getTypeMeta(type: PortDataType): TypeMetadata {
  return registry.get(type) ?? UNKNOWN_TYPE;
}

/**
 * Check if a type has metadata registered.
 */
export function hasTypeMeta(type: PortDataType): boolean {
  return registry.has(type);
}

/**
 * Get all registered type metadata.
 */
export function getAllTypeMeta(): readonly TypeMetadata[] {
  return Array.from(registry.values());
}

/**
 * Clear all custom types (keeps built-in types).
 */
export function resetTypeMeta(): void {
  registry.clear();
  for (const meta of BUILTIN_TYPES) {
    registry.set(meta.type, meta);
  }
}
